import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import Text from '../../../shared/Text';
import Tooltip from '../../../shared/Tooltip';
import { StyledSelect } from '../../../shared/FormStyles';
import { SelectWrapper } from '../../../shared/SettingsModal/StyledComponents';

export const ShareLinkModal = ({ onClose, campaigns }) => {
  const { t } = useTranslation('moments');
  const publicSlug = useSelector(state => state.currentBoard.publicSlug);
  const momentsLink = `https://${process.env.APP_HOST}/ard/${publicSlug}`;
  const [copied, setCopied] = useState(false);
  const [campaignOption, setCampaignOption] = useState(null);
  const urlLink = campaignOption ? campaignOption.value : momentsLink;

  const campaignOptions = campaigns.map(campaign => ({
    value: campaign.shareLink,
    label: campaign.name,
  }));

  const truncateText = text => {
    const maxLength = 55;

    if (text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }

    return text;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(urlLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Wrapper>
      <Tooltip
        id="copyTooltip"
        getContent={() =>
          copied ? t('shareLinkModal.success') : t('shareLinkModal.hover')
        }
      />
      <HeaderWrapper>
        <Text as="div" variant="h5">
          {t('shareLinkModal.header')}
        </Text>
        <CloseIcon onClick={onClose} />
      </HeaderWrapper>
      <BodyWrapper>
        <Text as="div" variant="body1">
          {t('shareLinkModal.body')}
        </Text>
      </BodyWrapper>

      <SelectContainer>
        <StyledSelect
          $width="600px"
          $padding="0 8px"
          onChange={value => setCampaignOption(value)}
          className="audience-filter"
          classNamePrefix="react-select"
          value={campaignOption}
          isClearable={true}
          options={campaignOptions}
        />
      </SelectContainer>

      <ReviewLinkWrapper>
        <Text color="black.80" variant="body2">
          {t('shareLinkModal.linkLabel')}
        </Text>
        <LinkContainer>
          <LinkInput readOnly placeholder={truncateText(urlLink)} />
          <CopyLink
            variant="small1"
            color="primary.100"
            onClick={handleCopy}
            data-tip
            data-for="copyTooltip"
          >
            {t('shareLinkModal.action')}
          </CopyLink>
        </LinkContainer>
      </ReviewLinkWrapper>
    </Wrapper>
  );
};

ShareLinkModal.propTypes = {
  onClose: PropTypes.func,
  campaigns: PropTypes.array,
};

const Wrapper = styled.div`
  padding: 4px;
`;

const HeaderWrapper = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
`;

const BodyWrapper = styled.div`
  text-align: left;
  margin-top: 20px;
`;

const ReviewLinkWrapper = styled.div`
  margin-top: 20px;
`;

const LinkContainer = styled.div`
  position: relative;
`;

const SelectContainer = styled(SelectWrapper)`
  margin-top: 10px;
`;

const LinkInput = styled.input`
  width: 100%;
  padding: 9px 16px;
  margin: 10px 0;
  border-radius: 8px;
  border: 1px solid;
  border-color: ${themeGet('colors.black.60')};
`;

const CopyLink = styled(Text)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;

const CloseIcon = styled.i.attrs({
  className: 'fa-regular fa-x',
})`
  cursor: pointer;
`;
